import { ActivityHeader } from '@components/Activity/ActivityHeader'
import { optionalEnrollmentId } from '@utils/optionalEnrollmentId'
import { ActivityCardMessage } from '@components/Activity/ActivityCardMessage'
import { ActivityDetails } from '@components/Activity/ActivityDetails'
import { DynamicQuestions } from '@components/Activity/DynamicQuestions'
import { LottieEffect } from '@components/LottieEffect'
import { View, Text, YStack, Spinner, isWeb, XStack } from 'tamagui'
import { QuestionFooter } from '@components/Activity/QuestionFooter'
import { useLocalSearchParams } from 'expo-router'
import { useGetActivity } from '@services/api/graphql/activity'
import { useScrollControl } from '@hooks/useScrollControl'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useActivityStore } from '@stores/useActivityStore'
import { useEnrollment } from '@hooks/useEnrollment'
import { ContentReportButton } from '@components/ContentReport/ContentReportButton'
import { EmptyStateComponent } from '@components/EmptyStateComponent'
import { useForm, FormProvider } from 'react-hook-form'

// Tipo para os dados do formulário
type FormData = {
  [questionId: string]: string | { [assertionId: string]: string }
}

interface IActivityPageProps {
  courseId?: string
  enrollmentId?: string
  activityId?: string
  onPreviousPress?: () => void
  onNextPress?: () => void
  hasPreviousContent?: boolean
  hasNextContent?: boolean
  isLoadingNextOrPreviousLesson?: boolean
  progressCardId: string
  updateEnrollmentProgress: () => void
}

export default function ActivityPage({
  courseId: propsCourseId,
  enrollmentId: propsEnrollmentId,
  activityId: propsActivityId,
  onPreviousPress,
  onNextPress,
  hasPreviousContent,
  hasNextContent,
  isLoadingNextOrPreviousLesson,
  progressCardId,
  updateEnrollmentProgress,
}: IActivityPageProps) {
  // Pega parâmetros da URL ou usa props
  const {
    courseId: paramCourseId,
    enrollmentId: paramEnrollmentId,
    activityId: paramActivityId,
  } = useLocalSearchParams<{
    courseId?: string
    enrollmentId?: string
    activityId?: string
  }>()

  const { setActivity, activity } = useActivityStore()
  const { enrollment } = useEnrollment()
  const [loading, setLoading] = useState(false)
  const [showActivityApproved, setShowActivityApproved] = useState(false)
  const { scrollToTop } = useScrollControl()

  const courseId = propsCourseId || paramCourseId
  const enrollmentId = propsEnrollmentId || paramEnrollmentId
  const activityId = propsActivityId || paramActivityId

  // Configuração do formulário
  const methods = useForm<FormData>({
    defaultValues: {},
  })

  // Hook do GraphQL
  const { data, error, getActivity } = useGetActivity({
    courseId: courseId ? Number(courseId) : 0,
    enrollmentId,
    id: activityId ? Number(activityId) : 0,
  })

  useEffect(() => {
    async function fetchActivity() {
      setLoading(true)
      const { data } = await getActivity({
        variables: {
          courseId: courseId ? Number(courseId) : 0,
          // Sem optionalEnrollmentId aqui o sentinela 0 chegava ao servidor,
          // que fazia Enrollment.find(0) e devolvia 404 com HTML — o Apollo
          // reportava "Erro ao carregar atividade" no preview do produtor.
          // As variables da chamada sobrescrevem as do hook, então a
          // normalização tem de estar nos dois lugares.
          enrollmentId: optionalEnrollmentId(enrollmentId),
          id: activityId ? Number(activityId) : 0,
        },
      })

      if (data?.activity) {
        setActivity(data.activity)
      }
      setLoading(false)
    }

    fetchActivity()
  }, [courseId, enrollmentId, activityId, getActivity, setActivity])

  const handleSubmitSuccess = useCallback(() => {
    scrollToTop()
  }, [scrollToTop])

  const handlePerfectScore = useCallback(() => {
    setShowActivityApproved(true)
    scrollToTop()
  }, [scrollToTop])

  const mode = useMemo(() => {
    const hasAnswer = !!activity?.examAnswer
    const canRetry =
      activity?.retryPermitted &&
      activity?.retryCount &&
      activity?.maxAttempts &&
      activity?.retryCount < activity?.maxAttempts

    if (hasAnswer && canRetry) return 'retry'

    if (!hasAnswer || canRetry) return 'edit'

    return 'readonly'
  }, [activity])

  if (loading) {
    return (
      <View flex={1} justifyContent="center" alignItems="center">
        <Spinner size="large" color="$primary-500" />
        <Text mt="$spacing-inset-xs" color="$text-secondary">
          Carregando atividade...
        </Text>
      </View>
    )
  }

  if (error) {
    return (
      <View
        flex={1}
        justifyContent="center"
        alignItems="center"
        p="$spacing-inset-lg"
      >
        <Text color="$error-500" textAlign="center" fontSize="$font-size-md">
          Erro ao carregar atividade
        </Text>
        <Text color="$text-secondary" textAlign="center" mt="$spacing-inset-xs">
          Tente novamente mais tarde
        </Text>
      </View>
    )
  }

  if (!data?.activity) {
    return (
      <View
        flex={1}
        justifyContent="center"
        alignItems="center"
        p="$spacing-inset-lg"
      >
        <Text color="$text-secondary" textAlign="center">
          Atividade não encontrada
        </Text>
      </View>
    )
  }

  // Prova sem questões: o count da lesson vem flaky do core, então só
  // confiamos no examQuestions autoritativo desta query. Vazio (e sem
  // loading/erro) = prova realmente sem questões cadastradas.
  if (!data.activity.examQuestions?.length) {
    return (
      <EmptyStateComponent
        title="Prova sem questões"
        description="Esta prova ainda não possui questões cadastradas."
        displayButton={false}
      />
    )
  }

  const activityTitle = data.activity.title

  return (
    // width 100% + minWidth 0: no desktop a prova fica dentro de uma XStack
    // (flex row). Sem isso a raiz herda flexShrink 0 / basis auto do RN e
    // cresce até o max-content do enunciado, estourando o card da aula.
    <View
      gap={'$spacing-inset-lg'}
      mb="$spacing-inset-lg"
      width="100%"
      minWidth={0}
      flexShrink={1}
    >
      {!isWeb && (
        <View position="relative">
          <ActivityHeader
            onPreviousPress={onPreviousPress}
            onNextPress={onNextPress}
            hasPreviousContent={hasPreviousContent}
            hasNextContent={hasNextContent}
            isLoading={isLoadingNextOrPreviousLesson}
          />
        </View>
      )}
      <FormProvider {...methods}>
        <YStack gap={'$spacing-inset-xs'} width="100%" minWidth={0}>
          <XStack
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            minWidth={0}
          >
            <Text
              ff="$mulishBold"
              fontSize="$font-size-md"
              color="$text-primary"
              fontWeight={700}
              flexShrink={1}
            >
              {activityTitle}
            </Text>

            <ContentReportButton
              schoolProductId={String(enrollment?.schoolProduct?.id)}
              courseId={String(courseId)}
            />
          </XStack>

          <View position="relative">
            {(mode === 'readonly' || mode === 'retry') && (
              <ActivityCardMessage
                progressCardId={progressCardId}
                enrollmentId={enrollmentId || ''}
                getActivity={getActivity}
                mode={mode}
                updateEnrollmentProgress={updateEnrollmentProgress}
              />
            )}
            {showActivityApproved && (
              <LottieEffect
                source={require('@assets/animations/activity_approved.json')}
                onComplete={() => setShowActivityApproved(false)}
              />
            )}
          </View>
        </YStack>

        <ActivityDetails />

        <DynamicQuestions />
        <QuestionFooter
          progressCardId={progressCardId}
          enrollmentId={enrollmentId || ''}
          getActivity={getActivity}
          mode={mode}
          updateEnrollmentProgress={updateEnrollmentProgress}
          onSubmitSuccess={handleSubmitSuccess}
          onPerfectScore={handlePerfectScore}
        />
      </FormProvider>
    </View>
  )
}
